import { useState } from "react";

import { AlertCircleIcon, CheckIcon, CopyIcon } from "lucide-react";

import { useCopyToClipboard } from "@hooks/use-copy-to-clipboard";

import {
  BuildInfo,
  buildInfo,
  formatBuildLabel,
  formatSupportDetails,
} from "@utils/buildInfo";
import { cn } from "@utils/ui";

type BuildInfoLineProps = {
  /** Injectable so tests do not depend on the ambient build environment. */
  info?: BuildInfo;
};

/**
 * Which deploy this session is running, at the foot of the user profile popover.
 *
 * Support asks for it on nearly every report, and "reload and tell me what it
 * says" is not something a user can do — so it is one click from copied. Sized
 * as a caption rather than a menu action: centred below a divider so it reads
 * as a footer rather than a fifth menu item.
 *
 * The accessible name carries the build label in every state, so a screen
 * reader user hears the id itself and not just "copy" (WCAG 2.5.3).
 */
export const BuildInfoLine = ({ info = buildInfo }: BuildInfoLineProps) => {
  const { copied, copy } = useCopyToClipboard();
  const [failed, setFailed] = useState(false);

  const label = formatBuildLabel(info);
  // A denied permission or a non-secure context (no `navigator.clipboard`) has
  // to say so — this is the one flow whose whole purpose is unblocking a
  // support call. The popover unmounts on close, which resets the state.
  const status = failed ? "Copy failed" : copied ? "Copied" : label;
  const action = failed ? "Copy failed" : copied ? "Copied" : "Copy";

  const handleCopy = () =>
    copy(formatSupportDetails(info), {
      onSuccess: () => setFailed(false),
      onError: () => setFailed(true),
    });

  return (
    <>
      <button
        type="button"
        title="Copy support details"
        aria-label={`${action} support details: ${label}`}
        className={cn(
          // Portaled out of the sidebar's `dark` scope (see UserCommandPopover),
          // so colors are hardcoded light values rather than `dark:` variants.
          "group mx-auto -mb-2 flex max-w-full items-center gap-1.5 rounded-md px-2 py-1",
          "text-[10px] font-medium tabular-nums leading-none tracking-tight",
          "cursor-pointer bg-transparent text-white/55",
          "transition-colors duration-150 hover:text-white/85",
          failed && "text-destructive hover:text-destructive"
        )}
        onClick={handleCopy}
      >
        {failed ? (
          <AlertCircleIcon className="h-2.5 w-2.5 shrink-0" />
        ) : copied ? (
          <CheckIcon className="h-2.5 w-2.5 shrink-0" />
        ) : (
          // Holds its box at `opacity-0` so revealing it on hover costs no
          // layout shift.
          <CopyIcon className="h-2.5 w-2.5 shrink-0 opacity-0 transition-opacity duration-150 group-hover:opacity-100" />
        )}
        <span className="truncate">{status}</span>
        {/* Mirrors the leading icon's box so the label sits on the true centre
            rather than being pushed left by half the icon + gap. */}
        <span aria-hidden className="h-2.5 w-2.5 shrink-0" />
      </button>

      {/* Outside the button: a live region nested inside an `aria-label`led
          element is not reliably announced. */}
      <span role="status" className="sr-only">
        {failed ? "Copy failed" : copied ? "Support details copied" : ""}
      </span>
    </>
  );
};
